import type { Band, HeldRow, TriageRow } from '../api/triage.ts';
import type { Assignee, Department } from '../api/taskQueries.ts';
import type { RecordKind } from '../api/tasks.ts';
import { recordTarget } from './model.ts';

/**
 * P-31 (A-#286; Arthur, 2A as refined): a triage row whose deadline a record completes offers THAT record's own form,
 * the one the client's page draws, with its own permissions, function and history; never a generic close. What
 * records it is the server's record_kind (deadline_recorded_by()), never a list of rules here. The button's words are
 * recordTarget()'s, the task card's own. A row the reader may not record, or one no record completes, says why in
 * words and offers release only.
 */
export type TriageClose =
  | { kind: RecordKind; label: string }
  | { why: string };

/** Who records each kind (each record's own route's words), for a reader who may not: release only (ruled Q2 A). */
export const RECORDED_BY: Record<RecordKind, string> = {
  filing: 'A filing is recorded by Compliance or tiers 1 and 2.',
  commission: 'A commission is recorded by Legal or tiers 1 and 2.',
  oath_designation: 'An oath and designation is recorded by Legal or tiers 1 and 2.',
  visit: 'A visit is recorded by a case manager on the client, the Director of Social Work, the Paralegal or tiers 1 and 2.',
  site_visit: 'A site visit is recorded by tiers 1 and 2, the Director of Real Estate and Property Management, and the Property team member whose task it is.',
  bond: 'A bond is recorded by Legal or tiers 1 and 2.',
  marshalling: 'Marshalling is recorded by the client’s Finance case manager, AP/AR, the Director of Finance or tiers 1 and 2.',
  statements: 'Statements are recorded by the client’s Finance case manager, AP/AR, the Director of Finance or tiers 1 and 2.',
  bank_refused: 'The bank’s answer is recorded by the client’s Finance case manager, AP/AR, the Director of Finance or tiers 1 and 2.',
  further_marshalling: 'Marshalling under an order is recorded by the client’s Finance case manager, AP/AR, the Director of Finance or tiers 1 and 2.',
  // 167 (P-24): a blocker's task has no deadline, so it never reaches triage; said for completeness
  commission_blocker: 'A commission blocker is cleared by the paralegal or tiers 1 and 2 recording what it waits on.',
};
/** 166 (P-33): marshalling has no single form: it is recorded bank by bank on the client's Finance section. */
export const MARSHALLING_WHY = 'Completed by the marshalling, recorded bank by bank on the client’s Finance section.';
/** A rule with no completion source (146): nothing on record can show it done. */
export const NO_SOURCE_WHY = 'No record shows this was done (no completion source is recorded). Release it to someone to do and complete.';
/** Any other rule with no record kind: completed by its task. */
export const TASK_WHY = 'Completed when its task is marked Complete: release it to someone.';

export function triageClose(r: Pick<TriageRow, 'client_id' | 'deadline_id' | 'record_kind' | 'may_record' | 'rule_code' |
  'source_known' | 'property_id' | 'court_order_id'>): TriageClose {
  const kind = r.record_kind ?? null;
  if (kind === null || r.deadline_id === null) {
    // since 162 (P-35) BOND_PROCURE is recorded by its bond (record_kind 'bond'), like any recorded kind
    return { why: r.source_known === false ? NO_SOURCE_WHY : TASK_WHY };
  }
  if (r.may_record !== true) return { why: RECORDED_BY[kind] };
  if (kind === 'marshalling') return { why: MARSHALLING_WHY };
  const target = recordTarget({ complete_by: kind, record: { kind, client_id: r.client_id, deadline_id: r.deadline_id,
    property_id: r.property_id ?? null, court_order_id: r.court_order_id ?? null } });
  return { kind, label: target?.label ?? 'Record it' };
}

/**
 * The triage screen's counting (146, rulings 3 and 5), pure so it is tested without a browser. The server makes
 * every number; this only adds them up for the screen, and says what a release act would make BEFORE it is
 * confirmed (ruling 5). Nothing here decides what may be released: the server refuses overdue and the hidden rules.
 */

export const AGES = ['1 to 30 days', '31 to 90 days', '91 days to a year', 'Over a year', 'Not yet due'] as const;

/** Whether a row can be released at all: never one with no deadline (F2: the date of death is not recorded). */
export function releasable(r: Pick<TriageRow, 'deadline_id' | 'why'>): boolean {
  return r.deadline_id !== null && r.why !== 'death_no_date';
}
export type Age = typeof AGES[number];

/**
 * A row's key on the screen: its deadline, or for a row with none, the client, why and label together (A-#140 B5).
 * Since 156 (A-#246) the rows with none (no date of death recorded) come from the server in their own block, shown once
 * in their own section above "To triage" (Triage.tsx, DeathsNoDate; A-#274), never among a department's rows; the old
 * "to the top" sort is gone.
 */
export function triageKey(r: Pick<TriageRow, 'deadline_id' | 'client_id' | 'why' | 'label'>): string {
  return r.deadline_id ?? `${r.client_id}|${r.why}|${r.label}`;
}

export function ageOf(daysOverdue: number | null): Age {
  if (daysOverdue === null || daysOverdue <= 0) return 'Not yet due';
  if (daysOverdue <= 30) return '1 to 30 days';
  if (daysOverdue <= 90) return '31 to 90 days';
  if (daysOverdue <= 365) return '91 days to a year';
  return 'Over a year';
}

/** The releasable triage rows counted by age, every age present, in order; a row that cannot be released is in none. */
export function countByAge(rows: readonly Pick<TriageRow, 'days_overdue' | 'deadline_id' | 'why'>[]): { age: Age; count: number }[] {
  const able = rows.filter(releasable);
  return AGES.map((age) => ({ age, count: able.filter((r) => ageOf(r.days_overdue) === age).length }));
}

export interface DepartmentSummary {
  department: Department;
  byBand: Record<Band, { held: number; triage_only: number }>;
  withheld: { hidden: number; exempt_visit: number };
}

/** The held summary as a table: one line per department, its three bands, and why the rest are withheld. */
export function summarise(rows: readonly HeldRow[]): DepartmentSummary[] {
  const out = new Map<Department, DepartmentSummary>();
  for (const r of rows) {
    const s = out.get(r.department) ?? {
      department: r.department,
      byBand: { within_14: { held: 0, triage_only: 0 }, later: { held: 0, triage_only: 0 }, overdue: { held: 0, triage_only: 0 } },
      withheld: { hidden: 0, exempt_visit: 0 },
    };
    s.byBand[r.band].held += r.held;
    s.byBand[r.band].triage_only += r.triage_only;
    s.withheld.hidden += r.withheld_hidden;
    s.withheld.exempt_visit += r.withheld_exempt_visit;
    out.set(r.department, s);
  }
  return [...out.values()];
}

/**
 * What a release act would make, by department, shown before it is confirmed (ruling 5): the HELD count of that band
 * (the triage-only ones never go by an act). department null: every department.
 */
export function releasePreview(rows: readonly HeldRow[], department: Department | null, band: 'within_14' | 'later'):
  { byDepartment: { department: Department; count: number }[]; total: number } {
  const by = summarise(rows)
    .filter((s) => department === null || s.department === department)
    .map((s) => ({ department: s.department, count: s.byBand[band].held }))
    .filter((d) => d.count > 0);
  return { byDepartment: by, total: by.reduce((n, d) => n + d.count, 0) };
}

/**
 * What triage's "Give them to" starts at (A-#145 (b)): the bands' rule, deadline_task_assignee(), for the selection.
 * Every selected row's default_to the same person, listed for the department: that person; all null: the queue
 * ('queue'). Rows that disagree, or a Primary not listed (no longer in the department), start at '' and the releaser
 * chooses. A choice made on the screen replaces it; the release sends exactly what is chosen.
 */
export function defaultAssignee(selected: readonly Pick<TriageRow, 'default_to'>[], listed: readonly Pick<Assignee, 'id'>[]):
  { to: string; why: 'primary' | 'queue' | 'mixed' | 'unlisted' | 'none' } {
  if (selected.length === 0) return { to: '', why: 'none' };
  const first = selected[0]!.default_to;
  if (selected.some((r) => r.default_to !== first)) return { to: '', why: 'mixed' };
  if (first === null) return { to: 'queue', why: 'queue' };
  return listed.some((p) => p.id === first) ? { to: first, why: 'primary' } : { to: '', why: 'unlisted' };
}

/**
 * What Triage keeps while its tab is open (P-30, A-#288): the department, the ticked rows, the person chosen and the
 * reason typed, in memory only (OpenTabs' useKeptState), never in the address or in storage. The reason is free text.
 */
export interface TriageKept {
  department: Department | null;
  selected: string[];
  pick: { key: string; to: string } | null;
  reason: string;
}

export const TRIAGE_FRESH: TriageKept = { department: null, selected: [], pick: null, reason: '' };

/** The department to show: the kept one while the reader may still triage it, else the first, as on a first visit. */
export function keptDepartment(kept: Department | null, departments: readonly Department[]): Department | null {
  return kept !== null && departments.includes(kept) ? kept : (departments[0] ?? null);
}

/**
 * The kept selection cut to the rows still releasable now (P-30): a deadline released or closed meanwhile drops out.
 * Before the rows are read (rows undefined) the kept ids stand.
 */
export function keptSelection(selected: readonly string[], rows: readonly Pick<TriageRow, 'deadline_id' | 'why'>[] | undefined): Set<string> {
  if (rows === undefined) return new Set(selected);
  const live = new Set(rows.filter(releasable).map((r) => r.deadline_id!));
  return new Set(selected.filter((id) => live.has(id)));
}

/** The select's two groups (A-#145 (c)): the department's own people first, then tiers 1-2 who are not in it. */
export function assigneeGroups<T extends Pick<Assignee, 'in_department'>>(people: readonly T[]): { own: T[]; others: T[] } {
  return { own: people.filter((p) => p.in_department), others: people.filter((p) => !p.in_department) };
}

/**
 * What the band release offers (A-#149): "Every department" only with may_release_all (tiers 1-2), then the
 * departments of may_release_in, in the usual order. A reader offered exactly one department and not all of them (a
 * director) is shown it as fixed text, never a one-choice select. start is where the act begins.
 */
export function releaseDepartments(all: boolean, departments: readonly Department[], order: readonly Department[]):
  { every: boolean; offered: Department[]; fixed: Department | null; start: Department | 'all' } {
  const offered = order.filter((d) => departments.includes(d));
  const fixed = !all && offered.length === 1 ? offered[0]! : null;
  return { every: all, offered, fixed, start: all ? 'all' : (offered[0] ?? 'all') };
}
